import React, { useState, useEffect } from 'react';
import { auth } from '../config/firebase';
import { signOut } from 'firebase/auth';
import { getStudentHistory } from '../services/googleSheets';
import { bookLesson, cancelLesson } from '../services/googleCalendar';

function StudentDashboard() {
  const [studentData, setStudentData] = useState(null);
  const [history, setHistory] = useState([]);
  const [selectedLanguage, setSelectedLanguage] = useState('en');
  const [showBooking, setShowBooking] = useState(false);

  useEffect(() => {
    loadStudentData();
  }, []);

  const loadStudentData = async () => {
    try {
      // TODO: 从 Firebase 获取学员信息
      const studentId = auth.currentUser?.uid;
      const data = await getStudentHistory(studentId);
      setHistory(data);

      // 临时数据
      setStudentData({
        id: 'JS-2024-001',
        name: '张三',
        totalHours: 24,
        completedHours: 18,
        progress: 75,
        nextLesson: '2026-02-05 10:00 AM',
        instructor: '李教练'
      });
    } catch (error) {
      console.error('加载数据失败:', error);
    }
  };

  const handleCancelLesson = async (lessonId) => {
    if (!window.confirm('确定要取消这节课吗？（仅限周一至周五下午3点前）')) {
      return;
    }

    try {
      await cancelLesson(lessonId);
      alert('课程已取消');
      loadStudentData();
    } catch (error) {
      alert(error.message);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* 顶部导航 */}
      <nav className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-4 flex justify-between items-center">
          <div>
            <h1 className="text-xl font-bold text-gray-900">My Learning Progress</h1>
            <p className="text-sm text-gray-600">Student ID: {studentData?.id}</p>
          </div>
          <div className="flex items-center gap-3">
            {/* 语言切换 */}
            <select
              value={selectedLanguage}
              onChange={(e) => setSelectedLanguage(e.target.value)}
              className="text-sm border border-gray-300 rounded px-2 py-1"
            >
              <option value="en">English</option>
              <option value="zh">中文</option>
              <option value="es">Español</option>
            </select>
            <button
              onClick={() => signOut(auth)}
              className="btn-secondary text-sm"
            >
              Sign Out
            </button>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* 学习进度卡片 */}
        <div className="card mb-6 fade-in">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold">Learning Overview</h2>
            <span className="badge badge-success">In Progress</span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <p className="text-sm text-gray-600 mb-1">Total Hours</p>
              <p className="text-3xl font-bold text-gray-900">{studentData?.totalHours}h</p>
            </div>
            <div>
              <p className="text-sm text-gray-600 mb-1">Completed</p>
              <p className="text-3xl font-bold text-green-600">{studentData?.completedHours}h</p>
            </div>
            <div>
              <p className="text-sm text-gray-600 mb-1">Progress</p>
              <div className="flex items-center gap-2">
                <div className="flex-1 bg-gray-200 rounded-full h-3">
                  <div
                    className="bg-blue-600 h-3 rounded-full transition-all"
                    style={{ width: `${studentData?.progress}%` }}
                  />
                </div>
                <span className="text-lg font-bold text-blue-600">{studentData?.progress}%</span>
              </div>
            </div>
          </div>

          <div className="mt-6 p-4 bg-blue-50 rounded-lg flex items-center justify-between">
            <div>
              <p className="text-sm text-blue-900 font-medium">Next Lesson</p>
              <p className="text-blue-700">{studentData?.nextLesson}</p>
              <p className="text-sm text-blue-600">Instructor: {studentData?.instructor}</p>
            </div>
            <button
              onClick={() => setShowBooking(true)}
              className="btn-primary"
            >
              Book Lesson
            </button>
          </div>
        </div>

        {/* Instructor 评价历史 */}
        <div className="card fade-in">
          <h2 className="text-lg font-semibold mb-4">Instructor Feedback History</h2>

          <div className="space-y-4">
            {history.length === 0 ? (
              <p className="text-gray-500 text-center py-8">No feedback available</p>
            ) : (
              history.map((entry, index) => (
                <div key={index} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <p className="font-semibold text-gray-900">{entry.date}</p>
                      <p className="text-sm text-gray-600">Instructor: {entry.instructor}</p>
                    </div>
                    <span className="badge badge-success">{entry.hours}h</span>
                  </div>

                  <div className="mt-3 p-3 bg-gray-50 rounded">
                    <p className="text-gray-800 leading-relaxed">
                      {selectedLanguage === 'en' 
                        ? entry.optimizedFeedback 
                        : entry.originalFeedback}
                    </p>
                  </div>

                  {entry.keyPoints && entry.keyPoints.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-2">
                      {entry.keyPoints.map((point, i) => (
                        <span key={i} className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded">
                          {point}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>

        {/* 预约弹窗 (简化版) */}
        {showBooking && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl max-w-md w-full p-6 fade-in">
              <h3 className="text-lg font-semibold mb-4">Book Lesson</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Select Date</label>
                  <input type="date" className="input" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Select Time</label>
                  <select className="input">
                    <option>09:00 AM</option>
                    <option>11:00 AM</option>
                    <option>02:00 PM</option>
                    <option>04:00 PM</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Preferred Instructor</label>
                  <select className="input">
                    <option>{studentData?.instructor}</option>
                    <option>Any Available</option>
                  </select>
                </div>
                
                <div className="flex gap-3">
                  <button
                    onClick={() => {
                      alert('Booking request sent. Waiting for office confirmation.');
                      setShowBooking(false);
                    }}
                    className="btn-primary flex-1"
                  >
                    Submit Booking
                  </button>
                  <button
                    onClick={() => setShowBooking(false)}
                    className="btn-secondary"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default StudentDashboard;
